/**
 * Client-side persistence for meta-progression, run bag, and singleplayer world.
 * Survives page refresh and client redeploys (localStorage).
 */

import type { DigPatch } from '../network/protocol';
import { ItemId } from '../items/catalog';
import {
  PickaxeTier,
  createDefaultProfile,
  createEmptyRunInventory,
  type ActiveRunInventory,
  type PersistentPlayerProfile,
  type Vector3D,
} from '../types';

export const PLAYER_SAVE_KEY = 'minelunky.playerSave.v1';
export const LAST_SESSION_KEY = 'minelunky.lastSession.v1';

export interface InventorySlotSnapshot {
  itemId: ItemId;
  amount: number;
}

export interface HotbarSnapshot {
  slots: Array<ItemId | null>;
  activeIndex: number;
}

export interface SingleplayerWorldSave {
  seed: number;
  runId: number;
  stage: number;
  digs: DigPatch[];
  position?: Vector3D;
  /** Dive bag at last SP save — kept separate from online sessions. */
  runBag?: InventorySlotSnapshot[];
  run?: ActiveRunInventory;
}

export interface PlayerSaveData {
  version: 1;
  profile: {
    playerId: string;
    displayName: string;
    walletCredits: number;
    unlockedPickaxeTiers: PickaxeTier[];
    equippedPickaxeTier: PickaxeTier;
    totalDeaths: number;
    totalExtractions: number;
    highestDepthReached: number;
    highestLevelReached: number;
  };
  bank: InventorySlotSnapshot[];
  ownedGear: ItemId[];
  hotbar: HotbarSnapshot;
  runBag: InventorySlotSnapshot[];
  run: ActiveRunInventory;
  singleplayer: SingleplayerWorldSave | null;
  updatedAt: number;
}

export type LastSessionHint =
  | { mode: 'singleplayer' }
  | { mode: 'host'; code: string }
  | { mode: 'join'; code: string };

const ITEM_IDS = new Set<string>(Object.values(ItemId));
const PICKAXE_TIERS = new Set<number>(Object.values(PickaxeTier).filter((v) => typeof v === 'number'));

function isItemId(value: unknown): value is ItemId {
  return typeof value === 'string' && ITEM_IDS.has(value);
}

function sanitizeSlots(raw: unknown): InventorySlotSnapshot[] {
  if (!Array.isArray(raw)) return [];
  const out: InventorySlotSnapshot[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const itemId = (entry as { itemId?: unknown }).itemId;
    const amount = (entry as { amount?: unknown }).amount;
    if (!isItemId(itemId) || typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
      continue;
    }
    out.push({ itemId, amount: Math.floor(amount) });
  }
  return out;
}

function sanitizeHotbar(raw: unknown): HotbarSnapshot {
  const fallback: HotbarSnapshot = {
    slots: [ItemId.PickaxeRusty, ItemId.SwordRusty, null, null, null],
    activeIndex: 0,
  };
  if (!raw || typeof raw !== 'object') return fallback;
  const slotsRaw = (raw as { slots?: unknown }).slots;
  const activeIndexRaw = (raw as { activeIndex?: unknown }).activeIndex;
  if (!Array.isArray(slotsRaw)) return fallback;
  const slots: Array<ItemId | null> = [null, null, null, null, null];
  for (let i = 0; i < 5; i++) {
    const value = slotsRaw[i];
    if (value === null || value === undefined) slots[i] = null;
    else if (isItemId(value)) slots[i] = value;
  }
  const activeIndex =
    typeof activeIndexRaw === 'number' && Number.isFinite(activeIndexRaw)
      ? Math.max(0, Math.min(4, Math.floor(activeIndexRaw)))
      : 0;
  if (!slots.some((s) => s !== null)) return fallback;
  return { slots, activeIndex: slots[activeIndex] ? activeIndex : slots.findIndex((s) => s !== null) };
}

function sanitizeOwnedGear(raw: unknown): ItemId[] {
  if (!Array.isArray(raw)) return [ItemId.PickaxeRusty, ItemId.SwordRusty];
  const gear = raw.filter(isItemId);
  if (!gear.includes(ItemId.PickaxeRusty)) gear.push(ItemId.PickaxeRusty);
  if (!gear.includes(ItemId.SwordRusty)) gear.push(ItemId.SwordRusty);
  return [...new Set(gear)];
}

function sanitizeRun(raw: unknown): ActiveRunInventory {
  const empty = createEmptyRunInventory();
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;
  return {
    dirt: typeof r.dirt === 'number' ? Math.max(0, Math.floor(r.dirt)) : 0,
    stone: typeof r.stone === 'number' ? Math.max(0, Math.floor(r.stone)) : 0,
    iron: typeof r.iron === 'number' ? Math.max(0, Math.floor(r.iron)) : 0,
    gold: typeof r.gold === 'number' ? Math.max(0, Math.floor(r.gold)) : 0,
    currentLevel:
      typeof r.currentLevel === 'number' ? Math.max(0, Math.floor(r.currentLevel)) : 0,
    depthReached:
      typeof r.depthReached === 'number' ? Math.max(0, Math.floor(r.depthReached)) : 0,
  };
}

function sanitizeDigs(raw: unknown): DigPatch[] {
  if (!Array.isArray(raw)) return [];
  const out: DigPatch[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const i = (entry as { i?: unknown }).i;
    const t = (entry as { t?: unknown }).t;
    if (typeof i !== 'number' || typeof t !== 'number') continue;
    if (!Number.isFinite(i) || !Number.isFinite(t) || i < 0) continue;
    out.push({ i: Math.floor(i), t: Math.floor(t) });
  }
  return out;
}

function sanitizeSingleplayer(raw: unknown): SingleplayerWorldSave | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.seed !== 'number' || !Number.isFinite(r.seed)) return null;
  const stage = typeof r.stage === 'number' ? Math.max(1, Math.floor(r.stage)) : 1;
  const runId = typeof r.runId === 'number' ? Math.max(0, Math.floor(r.runId)) : 0;
  let position: Vector3D | undefined;
  if (r.position && typeof r.position === 'object') {
    const p = r.position as Record<string, unknown>;
    if (
      typeof p.x === 'number' &&
      typeof p.y === 'number' &&
      typeof p.z === 'number' &&
      Number.isFinite(p.x) &&
      Number.isFinite(p.y) &&
      Number.isFinite(p.z)
    ) {
      position = { x: p.x, y: p.y, z: p.z };
    }
  }
  return {
    seed: r.seed >>> 0,
    runId,
    stage,
    digs: sanitizeDigs(r.digs),
    position,
    runBag: sanitizeSlots(r.runBag),
    run: sanitizeRun(r.run),
  };
}

function sanitizeProfile(
  raw: unknown,
  fallbackPlayerId: string,
  fallbackDisplayName: string,
): PlayerSaveData['profile'] {
  const base = createDefaultProfile(fallbackPlayerId, fallbackDisplayName);
  if (!raw || typeof raw !== 'object') {
    return {
      playerId: base.playerId,
      displayName: base.displayName,
      walletCredits: base.walletCredits,
      unlockedPickaxeTiers: [...base.unlockedPickaxeTiers],
      equippedPickaxeTier: base.equippedPickaxeTier,
      totalDeaths: base.totalDeaths,
      totalExtractions: base.totalExtractions,
      highestDepthReached: base.highestDepthReached,
      highestLevelReached: base.highestLevelReached,
    };
  }
  const r = raw as Record<string, unknown>;
  const tiersRaw = Array.isArray(r.unlockedPickaxeTiers) ? r.unlockedPickaxeTiers : [];
  const unlockedPickaxeTiers = tiersRaw
    .filter((t): t is PickaxeTier => typeof t === 'number' && PICKAXE_TIERS.has(t))
    .filter((t, i, arr) => arr.indexOf(t) === i);
  if (!unlockedPickaxeTiers.includes(PickaxeTier.Rusty)) {
    unlockedPickaxeTiers.unshift(PickaxeTier.Rusty);
  }
  const equipped =
    typeof r.equippedPickaxeTier === 'number' && PICKAXE_TIERS.has(r.equippedPickaxeTier)
      ? (r.equippedPickaxeTier as PickaxeTier)
      : PickaxeTier.Rusty;

  return {
    playerId:
      typeof r.playerId === 'string' && r.playerId.trim()
        ? r.playerId.trim()
        : fallbackPlayerId,
    displayName:
      typeof r.displayName === 'string' && r.displayName.trim()
        ? r.displayName.trim().slice(0, 24)
        : fallbackDisplayName,
    walletCredits:
      typeof r.walletCredits === 'number' && Number.isFinite(r.walletCredits)
        ? Math.max(0, Math.floor(r.walletCredits))
        : 0,
    unlockedPickaxeTiers,
    equippedPickaxeTier: unlockedPickaxeTiers.includes(equipped)
      ? equipped
      : PickaxeTier.Rusty,
    totalDeaths:
      typeof r.totalDeaths === 'number' ? Math.max(0, Math.floor(r.totalDeaths)) : 0,
    totalExtractions:
      typeof r.totalExtractions === 'number'
        ? Math.max(0, Math.floor(r.totalExtractions))
        : typeof r.totalExtracted === 'number'
          ? Math.max(0, Math.floor(r.totalExtracted))
          : 0,
    highestDepthReached:
      typeof r.highestDepthReached === 'number'
        ? Math.max(0, Math.floor(r.highestDepthReached))
        : 0,
    highestLevelReached:
      typeof r.highestLevelReached === 'number'
        ? Math.max(0, Math.floor(r.highestLevelReached))
        : 0,
  };
}

export function loadPlayerSave(
  playerId: string,
  displayName: string,
): PlayerSaveData | null {
  try {
    const raw = localStorage.getItem(PLAYER_SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlayerSaveData>;
    if (parsed.version !== 1) return null;
    return {
      version: 1,
      profile: sanitizeProfile(parsed.profile, playerId, displayName),
      bank: sanitizeSlots(parsed.bank),
      ownedGear: sanitizeOwnedGear(parsed.ownedGear),
      hotbar: sanitizeHotbar(parsed.hotbar),
      runBag: sanitizeSlots(parsed.runBag),
      run: sanitizeRun(parsed.run),
      singleplayer: sanitizeSingleplayer(parsed.singleplayer),
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function savePlayerSave(data: PlayerSaveData): void {
  try {
    const payload: PlayerSaveData = {
      ...data,
      version: 1,
      updatedAt: Date.now(),
    };
    localStorage.setItem(PLAYER_SAVE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode — ignore.
  }
}

export function clearSingleplayerWorldSave(): void {
  const existing = loadPlayerSave('local', 'Miner');
  if (!existing) return;
  existing.singleplayer = null;
  existing.run = createEmptyRunInventory();
  existing.runBag = [];
  savePlayerSave(existing);
}

export function hasContinuableSingleplayerSave(save: PlayerSaveData | null): boolean {
  return !!save?.singleplayer && save.singleplayer.stage >= 1;
}

export function loadLastSessionHint(): LastSessionHint | null {
  try {
    const raw = localStorage.getItem(LAST_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastSessionHint;
    if (parsed.mode === 'singleplayer') return { mode: 'singleplayer' };
    if (
      (parsed.mode === 'host' || parsed.mode === 'join') &&
      typeof parsed.code === 'string' &&
      /^\d{4}$/.test(parsed.code)
    ) {
      return { mode: parsed.mode, code: parsed.code };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveLastSessionHint(hint: LastSessionHint): void {
  try {
    localStorage.setItem(LAST_SESSION_KEY, JSON.stringify(hint));
  } catch {
    // ignore
  }
}

export function profileFromSave(
  save: PlayerSaveData,
  fallbackPlayerId: string,
  fallbackDisplayName: string,
): PersistentPlayerProfile {
  const p = save.profile;
  return {
    playerId: p.playerId || fallbackPlayerId,
    displayName: p.displayName || fallbackDisplayName,
    walletCredits: p.walletCredits,
    unlockedPickaxeTiers: new Set(p.unlockedPickaxeTiers),
    equippedPickaxeTier: p.equippedPickaxeTier,
    totalDeaths: p.totalDeaths,
    totalExtractions: p.totalExtractions,
    highestDepthReached: p.highestDepthReached,
    highestLevelReached: p.highestLevelReached,
  };
}
